/**
 * Phase 3 — WorkbenchOptimisationPanel
 *
 * Renders inside the Workbench when the active section is
 * "optimisation". Shows either:
 *   • a CTA to start a new study against the current scenario, OR
 *   • the most recent succeeded study's compact summary card.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, Plus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import optimisation from "@/api/optimisation";
import BaselineVsBestCard from "@/components/optimisation/BaselineVsBestCard";

export default function WorkbenchOptimisationPanel({ scenarioId, scenarioName }) {
    const [studies, setStudies] = useState([]);
    const [diff, setDiff] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!scenarioId) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const all = await optimisation.listStudies({ limit: 50 });
                if (cancelled) return;
                // Filter to studies whose baseline scenario matches this one,
                // newest first.
                const matched = (all || [])
                    .filter((s) => s?.baseline?.scenario_id === scenarioId)
                    .sort((a, b) =>
                        (b.created_at || "").localeCompare(a.created_at || ""),
                    );
                setStudies(matched);
                const succeeded = matched.find(
                    (s) => s.status === "succeeded" || s.status === "partial",
                );
                if (succeeded?.id) {
                    try {
                        const d = await optimisation.getDiff(succeeded.id);
                        if (!cancelled) setDiff({ ...d, study_name: succeeded.name });
                    } catch (_) {
                        // 409s are fine — older study with no recommendation.
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [scenarioId]);

    const startHref = `/run/optimisation/new?baseline_scenario_id=${encodeURIComponent(scenarioId || "")}`;

    return (
        <div className="p-6 space-y-4" data-testid="workbench-optimisation-panel">
            <header className="space-y-1">
                <div className="flex items-center gap-2">
                    <Crosshair className="w-4 h-4 text-ux-accent" />
                    <h2 className="text-[14px] font-medium text-ux-text-1">
                        Design optimisation
                    </h2>
                </div>
                <p className="text-[12px] text-ux-text-3">
                    Search the design space for a configuration that improves Pk
                    against this scenario at minimum design cost. Studies you
                    kick off here back-link to this scenario.
                </p>
            </header>

            {loading ? (
                <div
                    className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 text-[12px] text-ux-text-3"
                    data-testid="workbench-optimisation-loading"
                >
                    Loading studies…
                </div>
            ) : studies.length === 0 ? (
                <div
                    className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-6 text-center space-y-3"
                    data-testid="workbench-optimisation-cta"
                >
                    <p className="text-[13px] text-ux-text-2">
                        No design optimisation study has been run on this scenario yet.
                    </p>
                    <Button asChild data-testid="workbench-optimisation-start">
                        <Link to={startHref}>
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Start a design optimisation study
                        </Link>
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {diff && (
                        <>
                            <BaselineVsBestCard
                                diff={diff}
                                compact
                            />
                            {/* Phase 3.6 — compact composite-score line. */}
                            {(typeof diff?.recommended?.pk === "number"
                                || typeof diff?.recommended?.recommendation_score === "number") && (
                                <div
                                    className="text-[12px] font-mono text-ux-text-2 px-1"
                                    data-testid="workbench-optimisation-score-line"
                                >
                                    {typeof diff?.recommended?.pk === "number" && (
                                        <span>
                                            Pk {diff.recommended.pk.toFixed(2)}
                                        </span>
                                    )}
                                    {typeof diff?.recommended?.recommendation_score === "number" && (
                                        <span>
                                            {"   ·   "}
                                            Score {diff.recommended.recommendation_score.toFixed(2)}
                                            {typeof diff?.recommended?.recommendation_rank === "number" && (
                                                <span className="text-ux-text-3">
                                                    {" "}(rank {diff.recommended.recommendation_rank})
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    <div
                        className="rounded-md border border-ux-border-1 bg-ux-bg-1 divide-y divide-ux-border-1"
                        data-testid="workbench-optimisation-studies"
                    >
                        {studies.slice(0, 5).map((s) => (
                            <div
                                key={s.id}
                                className="flex items-center gap-3 p-3"
                                data-testid={`workbench-optimisation-study-${s.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2">
                                        <Link
                                            to={`/run/optimisation/${s.id}`}
                                            className="text-[13px] text-ux-text-1 hover:underline truncate"
                                        >
                                            {s.name}
                                        </Link>
                                        <StatusPill status={s.status} />
                                    </div>
                                    <p className="text-[11px] text-ux-text-3 font-mono truncate">
                                        {s.id} · {s.profile} · {s.created_at?.slice(0, 16) || "—"}
                                    </p>
                                </div>
                                <Link
                                    to={`/run/optimisation/${s.id}`}
                                    className="text-[11px] text-ux-accent inline-flex items-center gap-1 hover:underline"
                                >
                                    Open <ExternalLink className="w-3 h-3" />
                                </Link>
                            </div>
                        ))}
                    </div>
                    <div>
                        <Button asChild variant="outline" size="sm">
                            <Link to={startHref}>
                                <Plus className="w-3 h-3 mr-1" /> Start another study
                            </Link>
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
